import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import RankBadge from '../components/RankBadge';
import { 
    User, 
    Building2, 
    Wrench, 
    Mail, 
    Calendar,
    TrendingUp,
    Lock,
    FileText,
    ArrowRight,
    MapPin,
    ShieldCheck,
    Loader2,
    Award,
    Star,
    Share2,
    Copy,
    Users,
    Gift
} from 'lucide-react';
import { formatDate, formatDateShort } from '../lib/utils';
import { reviewAPI, referralAPI } from '../lib/api';
import { toast } from 'sonner';

// Full rank data with point thresholds - 6-level Call-of-Duty style
const RANK_DATA = [
    { name: 'Rookie', minPoints: 0, maxPoints: 19, desc: 'New to the platform. Building your first reviews.' },
    { name: 'Journeyman', minPoints: 20, maxPoints: 49, desc: 'Gaining experience and contributing to the network.' },
    { name: 'Pro', minPoints: 50, maxPoints: 99, desc: 'Established contributor with solid platform standing.' },
    { name: 'Veteran', minPoints: 100, maxPoints: 174, desc: 'Experienced professional with proven track record.' },
    { name: 'Foreman', minPoints: 175, maxPoints: 274, desc: 'High-impact contractor leading by example.' },
    { name: 'Legend', minPoints: 275, maxPoints: Infinity, desc: 'Elite status. Protecting the trades.' },
];

const Profile = () => {
    const { user } = useAuth();
    const currentRankIndex = RANK_DATA.findIndex(r => r.name === user?.rank_name) || 0;
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [referralData, setReferralData] = useState(null);
    const [loadingReferral, setLoadingReferral] = useState(true);

    useEffect(() => {
        loadReviewData();
        loadReferralData();
    }, []);

    const loadReviewData = async () => {
        try {
            const [reviewsRes, statsRes] = await Promise.all([
                reviewAPI.getMyReviews(),
                reviewAPI.getMyStats()
            ]);
            setReviews(reviewsRes.data || []);
            setStats(statsRes.data);
        } catch (err) {
            console.error('Failed to load reviews:', err);
        } finally {
            setLoadingReviews(false);
        }
    };

    const loadReferralData = async () => {
        try {
            const response = await referralAPI.getCode();
            setReferralData(response.data);
        } catch (err) {
            console.error('Failed to load referral data:', err);
        } finally {
            setLoadingReferral(false);
        }
    };

    const copyReferralLink = () => {
        if (referralData?.referral_link) {
            navigator.clipboard.writeText(referralData.referral_link);
            toast.success('Referral link copied to clipboard!');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
            pending: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
            hidden: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
            flagged: 'bg-red-500/15 text-red-600 dark:text-red-400'
        };
        const labels = { approved: 'Active', pending: 'Pending', hidden: 'Hidden', flagged: 'Flagged' };
        return (
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    // Get rank badge color based on rank name
    const getRankBadgeColor = (rankName) => {
        const colors = {
            'Rookie': 'bg-slate-500',
            'Journeyman': 'bg-emerald-600',
            'Pro': 'bg-blue-500',
            'Veteran': 'bg-amber-500',
            'Foreman': 'bg-purple-500',
            'Legend': 'bg-primary'
        };
        return colors[rankName] || 'bg-gray-600';
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="font-heading font-extrabold text-3xl text-foreground mb-8">
                    Your Profile
                </h1>

                {/* Beta Badges */}
                {(user?.founding_contractor || user?.early_adopter) && (
                    <div className="flex flex-wrap gap-2 mb-6">
                        {user?.founding_contractor && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-lg">
                                <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                                <span className="font-semibold text-amber-600 dark:text-amber-400">Founding Contractor</span>
                            </div>
                        )}
                        {user?.early_adopter && !user?.founding_contractor && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-lg">
                                <Award className="h-5 w-5 text-primary" />
                                <span className="font-semibold text-primary">Early Adopter</span>
                            </div>
                        )}
                        {user?.lifetime_free && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                                <span className="font-semibold text-emerald-600 dark:text-emerald-400">Lifetime Free</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Profile Card with Badge */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader>
                        <div className="flex items-center gap-6">
                            {/* Large Rank Badge */}
                            <div className="flex-shrink-0">
                                <RankBadge 
                                    rank={user?.rank_name || 'Rookie'} 
                                    size="xl" 
                                    showGlow={true}
                                />
                            </div>
                            <div className="flex-1">
                                <CardTitle className="font-heading font-bold text-2xl text-foreground">
                                    {user?.company_name}
                                </CardTitle>
                                <Badge className={`mt-2 ${getRankBadgeColor(user?.rank_name)}`}>
                                    {user?.rank_name || 'Rookie'}
                                </Badge>
                                <p className="text-sm text-muted-foreground mt-2 italic">
                                    "{user?.rank_description || 'New to the platform. Building your first reviews.'}"
                                </p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-accent">
                                    <Mail className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Email</p>
                                    <p className="font-medium text-foreground">{user?.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-accent">
                                    <Building2 className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Company</p>
                                    <p className="font-medium text-foreground">{user?.company_name}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-accent">
                                    <Wrench className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Trade</p>
                                    <p className="font-medium text-foreground">{user?.contractor_trade}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-lg bg-accent">
                                    <Calendar className="h-5 w-5 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Member Since</p>
                                    <p className="font-medium text-foreground">{formatDate(user?.created_at)}</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Rank Progress Card */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Rank Progress
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                            <div className="text-center p-6 bg-accent/30 rounded-lg">
                                <p className="text-4xl font-heading font-extrabold text-primary tabular-nums">
                                    {user?.rank_points || 0}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">Total Points</p>
                            </div>

                            <div className="text-center p-6 bg-accent/30 rounded-lg">
                                <p className="text-4xl font-heading font-extrabold text-foreground tabular-nums">
                                    {user?.approved_reviews_count || 0}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">Approved Reviews</p>
                            </div>

                            <div className="text-center p-6 bg-accent/30 rounded-lg">
                                <p className="text-4xl font-heading font-extrabold text-foreground tabular-nums">
                                    {user?.points_to_next_rank || 0}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">Points to Next Rank</p>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        {user?.next_rank_name && (
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-medium text-foreground">{user?.rank_name}</span>
                                    <span className="text-muted-foreground">{user?.next_rank_name}</span>
                                </div>
                                <Progress value={user?.progress_percent || 0} className="h-3" />
                                <p className="text-xs text-muted-foreground text-center">
                                    {user?.progress_percent || 0}% progress to next rank
                                </p>
                            </div>
                        )}
                        {!user?.next_rank_name && (
                            <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/30">
                                <p className="text-sm font-medium text-primary">
                                    You've reached the highest rank: Trades Guardian!
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Your Review History Section */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                                <FileText className="h-5 w-5 text-primary" />
                                Your Review History
                            </CardTitle>
                            <Link to="/reviews/history">
                                <Button variant="outline" size="sm">
                                    View All
                                    <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {/* Stats Row */}
                        {stats && (
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="p-3 bg-accent/30 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                                    <p className="text-xs text-muted-foreground">Submitted</p>
                                </div>
                                <div className="p-3 bg-accent/30 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-emerald-500">{stats.approved}</p>
                                    <p className="text-xs text-muted-foreground">Active</p>
                                </div>
                                <div className="p-3 bg-accent/30 rounded-lg text-center">
                                    <p className="text-2xl font-bold text-primary">{stats.verified_count}</p>
                                    <p className="text-xs text-muted-foreground">Verified</p>
                                </div>
                            </div>
                        )}

                        {/* Recent Reviews Preview */}
                        {loadingReviews ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                        ) : reviews.length > 0 ? (
                            <div className="space-y-2">
                                <p className="text-xs text-muted-foreground mb-2">Most Recent Reviews</p>
                                {reviews.slice(0, 3).map((review) => (
                                    <div 
                                        key={review.id}
                                        className="flex items-center justify-between p-3 bg-accent/30 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {review.address_street || review.job_type}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                    <span>{formatDateShort(review.created_at)}</span>
                                                    {review.has_verified_evidence && (
                                                        <span className="flex items-center gap-0.5 text-emerald-500">
                                                            <ShieldCheck className="h-3 w-3" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {getStatusBadge(review.status)}
                                        </div>
                                    </div>
                                ))}
                                {reviews.length > 3 && (
                                    <p className="text-xs text-muted-foreground text-center pt-2">
                                        +{reviews.length - 3} more reviews
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">No reviews submitted yet</p>
                                <Link to="/">
                                    <Button variant="outline" size="sm" className="mt-3">
                                        Submit Your First Review
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Referral Program Card */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                            <Share2 className="h-5 w-5 text-primary" />
                            Invite Contractors
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loadingReferral ? (
                            <div className="flex justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            </div>
                        ) : (
                            <>
                                {/* Referral Link */}
                                <div className="mb-4">
                                    <p className="text-sm text-muted-foreground mb-2">Your referral link:</p>
                                    <div className="flex gap-2">
                                        <Input
                                            readOnly
                                            value={referralData?.referral_link || ''}
                                            className="bg-accent font-mono text-sm"
                                            data-testid="referral-link-input"
                                        />
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={copyReferralLink}
                                            className="flex-shrink-0"
                                            data-testid="copy-referral-btn"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Share this link with fellow contractors. You'll earn +50 rank points and a bonus invite code when they submit their first review!
                                    </p>
                                </div>

                                {/* Referral Stats */}
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="p-3 bg-accent/30 rounded-lg text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <Users className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <p className="text-2xl font-bold text-foreground">{referralData?.referral_count || 0}</p>
                                        <p className="text-xs text-muted-foreground">Referrals</p>
                                    </div>
                                    <div className="p-3 bg-accent/30 rounded-lg text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <p className="text-2xl font-bold text-primary">+{(referralData?.referral_count || 0) * 50}</p>
                                        <p className="text-xs text-muted-foreground">Bonus Points</p>
                                    </div>
                                    <div className="p-3 bg-accent/30 rounded-lg text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <Gift className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <p className="text-2xl font-bold text-emerald-500">{referralData?.referral_count || 0}</p>
                                        <p className="text-xs text-muted-foreground">Invite Codes</p>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Rank Ladder with Badges */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground">
                            Contractor Rank Ladder
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                            <em>For the Trades. By the Trades. Protecting the trades.</em>
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {RANK_DATA.map((rank, index) => {
                                const isCurrentRank = user?.rank_name === rank.name;
                                const isUnlocked = index <= currentRankIndex;
                                const isNextRank = index === currentRankIndex + 1;
                                
                                return (
                                    <div 
                                        key={rank.name}
                                        className={`
                                            relative flex items-center gap-4 p-3 rounded-lg transition-all
                                            ${isCurrentRank 
                                                ? 'bg-primary/10 border-2 border-primary/50 shadow-lg' 
                                                : isUnlocked 
                                                    ? 'bg-accent/30 border border-border'
                                                    : 'bg-accent/10 border border-border/50'
                                            }
                                        `}
                                    >
                                        {/* Rank Badge */}
                                        <div className="flex-shrink-0 relative">
                                            <RankBadge 
                                                rank={rank.name} 
                                                size="sm" 
                                                locked={!isUnlocked}
                                                showGlow={isCurrentRank}
                                            />
                                            {!isUnlocked && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <Lock className="h-4 w-4 text-muted-foreground/60" />
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Rank Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`
                                                    font-semibold text-sm
                                                    ${isCurrentRank ? 'text-primary' : isUnlocked ? 'text-foreground' : 'text-muted-foreground'}
                                                `}>
                                                    {index + 1}. {rank.name}
                                                </span>
                                                {isCurrentRank && (
                                                    <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-bold">
                                                        YOU
                                                    </span>
                                                )}
                                                {isNextRank && (
                                                    <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-bold">
                                                        NEXT
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`
                                                text-xs mt-0.5 line-clamp-1
                                                ${isUnlocked ? 'text-muted-foreground' : 'text-muted-foreground/50'}
                                            `}>
                                                {rank.desc}
                                            </p>
                                        </div>
                                        
                                        {/* Points Required */}
                                        <div className={`
                                            text-xs font-medium flex-shrink-0
                                            ${isUnlocked ? 'text-muted-foreground' : 'text-muted-foreground/50'}
                                        `}>
                                            {rank.maxPoints === Infinity 
                                                ? `${rank.minPoints}+ pts` 
                                                : `${rank.minPoints}-${rank.maxPoints} pts`
                                            }
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* How to earn points */}
                        <div className="mt-6 p-4 bg-accent/20 rounded-lg border border-border">
                            <p className="font-medium text-foreground text-sm mb-2">How to earn points:</p>
                            <ul className="text-xs text-muted-foreground space-y-1">
                                <li className="flex items-center gap-2">
                                    <span className="text-emerald-500 font-bold">+10</span>
                                    <span>points per approved review</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-blue-500 font-bold">+1</span>
                                    <span>point per month of membership (max 20)</span>
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="text-red-500 font-bold">-15</span>
                                    <span>points per moderation strike</span>
                                </li>
                            </ul>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default Profile;
